import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle, Clapperboard, Download, Image as ImageIcon,
  MessageSquare, Play, Plus, RefreshCw, Scissors, Search, Shield, Sparkles,
  Type, User, Video, Wand2, Workflow,
} from 'lucide-react';

function FlowNode({ className = '', icon: Icon, title, status, children }) {
  return (
    <div className={`absolute w-[236px] overflow-hidden rounded-2xl border border-[#ddd4c5] bg-[#fffdf8] shadow-[0_18px_42px_rgba(76,64,44,0.14)] ${className}`}>
      <div className="flex items-center gap-2 border-b border-[#ebe3d6] bg-[#f8f3ea] px-3 py-2 text-[12px] text-stone-500">
        <Icon size={13} className="text-[#7d6ca3]" />
        <span className="min-w-0 flex-1 truncate font-medium text-stone-700">{title}</span>
        {status && <span className="rounded-full bg-white px-2 py-0.5 text-[10px] text-[#6f6196]">{status}</span>}
      </div>
      {children}
      <span className="absolute -left-1.5 top-1/2 h-3 w-3 rounded-full border-2 border-[#f4f1e9] bg-[#8c7db0]" />
      <span className="absolute -right-1.5 top-1/2 h-3 w-3 rounded-full border-2 border-[#f4f1e9] bg-[#8c7db0]" />
    </div>
  );
}

function TextCard({ className, title, status, content, tags }) {
  return (
    <FlowNode className={className} icon={Type} title={title} status={status}>
      <div className="space-y-2 px-4 py-4 text-[13px] leading-6 text-stone-700">
        <p>{content}</p>
        <div className="flex flex-wrap gap-1.5 pt-1">
          {tags.map(tag => <span key={tag} className="rounded-full bg-[#eee9de] px-2 py-1 text-[11px] text-stone-500">{tag}</span>)}
        </div>
      </div>
    </FlowNode>
  );
}

function ImageCard({ className, title, status, caption, meta, gradient }) {
  return (
    <FlowNode className={className} icon={ImageIcon} title={title} status={status}>
      <div className={`relative h-[158px] overflow-hidden bg-gradient-to-br ${gradient}`}>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,.65),transparent_34%),radial-gradient(circle_at_75%_70%,rgba(0,0,0,.18),transparent_38%)]" />
        <div className="absolute bottom-3 left-3 right-3 rounded-xl border border-white/25 bg-black/25 p-2 text-white backdrop-blur-sm">
          <p className="text-[12px] font-semibold">{caption}</p>
          <p className="mt-0.5 text-[10px] text-white/70">{meta}</p>
        </div>
      </div>
    </FlowNode>
  );
}

function VideoCard({ className, title, status, caption, duration, gradient }) {
  return (
    <FlowNode className={className} icon={Video} title={title} status={status}>
      <div className={`relative flex h-[148px] items-center justify-center overflow-hidden bg-gradient-to-br ${gradient}`}>
        <div className="absolute inset-0 bg-black/20" />
        <div className="relative flex h-12 w-12 items-center justify-center rounded-full bg-white/90 text-[#6f6196] shadow-xl">
          <Play size={18} fill="currentColor" />
        </div>
        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between rounded-xl bg-black/30 px-3 py-2 text-[11px] text-white backdrop-blur-sm">
          <span>{caption}</span>
          <span>{duration}</span>
        </div>
      </div>
    </FlowNode>
  );
}

function StagePill({ active, children }) {
  return (
    <span className={`rounded-full border px-3 py-1.5 text-[12px] ${active ? 'border-[#c9bedc] bg-white text-[#6f6196]' : 'border-[#ddd4c5] bg-white/70 text-stone-400'}`}>
      {children}
    </span>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-[#e2d8ca] bg-white/70 px-3 py-2">
      <p className="text-[11px] text-stone-400">{label}</p>
      <p className="mt-1 text-[15px] font-semibold text-stone-800">{value}</p>
    </div>
  );
}

function LineLayer() {
  return (
    <svg className="absolute left-0 top-0 h-[900px] w-[1380px] overflow-visible" viewBox="0 0 1380 900" fill="none">
      <defs>
        <marker id="arrow" markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto" markerUnits="strokeWidth">
          <path d="M0,0 L0,6 L9,3 z" fill="#8c7db0" />
        </marker>
      </defs>
      {[
        'M250 190 C310 190 315 160 350 160',
        'M250 190 C310 190 315 370 350 370',
        'M570 160 C620 160 635 245 670 245',
        'M570 370 C620 370 635 280 670 280',
        'M890 260 C940 260 955 260 1000 260',
        'M250 660 C305 660 320 660 350 660',
      ].map(d => (
        <path key={d} d={d} stroke="#8c7db0" strokeWidth="2" strokeDasharray="7 7" markerEnd="url(#arrow)" />
      ))}
    </svg>
  );
}

export default function PrototypeCanvasPage() {
  const navigate = useNavigate();

  return (
    <div className="h-screen overflow-hidden bg-[#f4f1e9] text-stone-900">
      <header className="flex h-14 items-center gap-3 border-b border-[#dfd6c8] bg-[#fbf8f1]/95 px-4 backdrop-blur">
        <button onClick={() => navigate('/studio')} className="flex h-9 w-9 items-center justify-center rounded-xl border border-[#ddd4c5] bg-white text-stone-500 hover:text-[#6f6196]">
          <ArrowLeft size={16} />
        </button>
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#eee9de] text-[#6f6196]">
          <Workflow size={17} />
        </div>
        <div>
          <h1 className="text-[15px] font-semibold">无限画布 · 前端 UI 预览</h1>
          <p className="text-[11px] text-stone-400">不连接数据库 · 模拟短剧生产链路</p>
        </div>
        <div className="ml-4 hidden items-center gap-2 rounded-full border border-[#ddd4c5] bg-white/70 px-3 py-1.5 text-[12px] text-stone-500 md:flex">
          <Search size={13} /> 搜索节点 / 片段 / 资产
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button className="rounded-xl border border-[#ddd4c5] bg-white px-3 py-2 text-[12px] text-stone-600 hover:bg-[#f5f0e7]"><Plus size={13} className="mr-1 inline" />添加节点</button>
          <button className="rounded-xl bg-[#8c7db0] px-3 py-2 text-[12px] font-medium text-white shadow-lg shadow-[#8c7db0]/20"><Wand2 size={13} className="mr-1 inline" />Agent 建图</button>
        </div>
      </header>

      <div className="grid h-[calc(100vh-56px)] grid-cols-[1fr_360px]">
        <main className="relative overflow-hidden bg-[#f4f1e9]">
          <div className="absolute inset-0 bg-[linear-gradient(rgba(140,125,176,.08)_1px,transparent_1px),linear-gradient(90deg,rgba(140,125,176,.08)_1px,transparent_1px)] bg-[size:28px_28px]" />
          <div className="absolute left-4 top-4 z-20 flex gap-2">
            {['剧本', '资产', '首帧'].map(item => <StagePill key={item} active>{item}</StagePill>)}
            {['视频', '交付'].map(item => <StagePill key={item}>{item}</StagePill>)}
          </div>

          <div className="absolute left-10 top-16 h-[430px] w-[1320px] rounded-[28px] border border-dashed border-[#c9bedc]/80 bg-[#eee9de]/45 p-5" />
          <div className="absolute left-16 top-20 z-10 inline-flex items-center gap-2 rounded-xl border border-[#c9bedc] bg-[#fbf8f1]/90 px-3 py-2 text-[13px] font-semibold text-[#6f6196] shadow-sm">
            <Workflow size={14} /> EP12 · 开场追逃 <span className="text-[11px] font-normal text-stone-400">剧本 → 资产 → 首帧 → 视频</span>
          </div>
          <div className="absolute left-10 top-[520px] h-[300px] w-[1320px] rounded-[28px] border border-dashed border-[#c9bedc]/80 bg-[#eee9de]/35 p-5" />
          <div className="absolute left-16 top-[530px] z-10 inline-flex items-center gap-2 rounded-xl border border-[#c9bedc] bg-[#fbf8f1]/90 px-3 py-2 text-[13px] font-semibold text-[#6f6196] shadow-sm">
            <Workflow size={14} /> 下一段建议 <span className="text-[11px] font-normal text-stone-400">Agent 自动补片段</span>
          </div>

          <div className="absolute left-10 top-16 h-[900px] w-[1380px] origin-top-left scale-[0.86] 2xl:scale-100">
            <LineLayer />
            <TextCard className="left-[20px] top-[120px]" title="剧本文本" status="已确认" content="雨夜，苏照影冲进旧巷，身后追兵火把逼近。她回头看见叶长歌挡在巷口。" tags={['冲突', '雨夜', '单人反应']} />
            <ImageCard className="left-[340px] top-[95px]" title="人物资产 · 苏照影" status="资产完成" caption="冷色写实人物参考" meta="9:16 · Seedream" gradient="from-[#4f6c87] via-[#9ab0bf] to-[#d7b08a]" />
            <ImageCard className="left-[340px] top-[310px]" title="场景资产 · 旧巷雨夜" status="待确认" caption="低饱和电影场景" meta="环境 / 光线 / 构图" gradient="from-[#23262f] via-[#5f6f7f] to-[#b07e59]" />
            <ImageCard className="left-[660px] top-[185px] ring-4 ring-[#8c7db0]/15" title="首帧 · 回头瞬间" status="首帧完成" caption="人物 + 场景合成首帧" meta="角色一致性 92%" gradient="from-[#6f6196] via-[#b98f98] to-[#d8b36d]" />
            <VideoCard className="left-[990px] top-[185px]" title="视频片段 01" status="待验收" caption="雨夜回头 · 5s" duration="00:05" gradient="from-[#1c1d22] via-[#59646e] to-[#c2905f]" />
            <TextCard className="left-[20px] top-[615px]" title="Agent 建议" status="可执行" content="下一段建议切到叶长歌单人反应：先看火把，再压低剑尖，制造保护感。" tags={['补镜头', '关系推进']} />
            <ImageCard className="left-[340px] top-[600px]" title="首帧草案 · 叶长歌" status="待生成" caption="国风 CG 角色镜头" meta="单人反应 / 半身" gradient="from-[#26243a] via-[#7fafa8] to-[#e0c28e]" />
          </div>

          <div className="absolute bottom-5 left-5 flex overflow-hidden rounded-xl border border-[#ddd4c5] bg-white/90 shadow-lg">
            {['+', '−', '定位', '小地图'].map(item => <button key={item} className="border-r border-[#eee6d8] px-3 py-2 text-[12px] text-stone-500 last:border-r-0">{item}</button>)}
          </div>
        </main>

        <aside className="flex min-h-0 flex-col border-l border-[#dfd6c8] bg-[#fbf8f1]">
          <div className="border-b border-[#e4dccf] p-4">
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#eee9de] text-[#6f6196]"><MessageSquare size={16} /></div>
              <div>
                <p className="text-[14px] font-semibold">画布 Agent</p>
                <p className="text-[11px] text-stone-400">负责拆镜、补资产、检查交付</p>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2">
              <Stat label="节点" value="7" />
              <Stat label="待确认" value="2" />
              <Stat label="点数预估" value="288" />
            </div>
          </div>

          <div className="min-h-0 flex-1 space-y-4 overflow-auto p-4">
            <section className="rounded-2xl border border-[#e2d8ca] bg-white/70 p-4">
              <p className="mb-3 text-[13px] font-semibold text-stone-700">当前阶段</p>
              <div className="space-y-2">
                {[[CheckCircle, '剧本拆镜', '完成'], [ImageIcon, '人物/场景资产', '2 个待确认'], [Sparkles, '首帧生成', '1 个完成'], [Video, '视频提交', '等待验收']].map(([Icon, label, status]) => (
                  <div key={label} className="flex items-center gap-2 rounded-xl bg-[#f7f2e9] px-3 py-2 text-[12px]"><Icon size={13} className="text-[#6f6196]" /><span className="flex-1 text-stone-700">{label}</span><span className="text-stone-400">{status}</span></div>
                ))}
              </div>
            </section>

            <section className="rounded-2xl border border-[#e2d8ca] bg-white/70 p-4">
              <p className="mb-3 text-[13px] font-semibold text-stone-700">生产动作</p>
              <div className="grid grid-cols-2 gap-2">
                {[[CheckCircle, '确认待生成'], [ImageIcon, '生成资产'], [Sparkles, '生成首帧'], [Clapperboard, '提交视频']].map(([Icon, label], index) => (
                  <button key={label} className={`rounded-xl border border-[#ddd4c5] px-3 py-3 text-left text-[12px] text-stone-700 ${index === 0 ? 'bg-[#eee9de]' : 'bg-white'}`}><Icon size={14} className="mb-1 text-[#6f6196]" />{label}</button>
                ))}
              </div>
            </section>

            <section className="rounded-2xl border border-[#e2d8ca] bg-white/70 p-4">
              <div className="mb-3 flex items-center"><p className="text-[13px] font-semibold text-stone-700">交付检查</p><button className="ml-auto text-[11px] text-[#6f6196]"><RefreshCw size={12} className="mr-1 inline" />刷新</button></div>
              <div className="space-y-2 text-[12px] text-stone-600">
                <div className="flex items-center gap-2 rounded-xl bg-[#f7f2e9] px-3 py-2"><Shield size={13} className="text-emerald-600" />一致性通过<span className="ml-auto text-stone-400">92%</span></div>
                <div className="flex items-center gap-2 rounded-xl bg-[#f7f2e9] px-3 py-2"><Scissors size={13} className="text-amber-600" />需补镜头<span className="ml-auto text-stone-400">1 个</span></div>
                <div className="flex items-center gap-2 rounded-xl bg-[#f7f2e9] px-3 py-2"><Download size={13} className="text-[#6f6196]" />可导出报告<span className="ml-auto text-stone-400">JSON / CSV</span></div>
              </div>
            </section>
          </div>

          <div className="border-t border-[#e4dccf] p-4">
            <div className="rounded-2xl border border-[#d9d0c2] bg-white p-3">
              <textarea className="h-20 w-full resize-none bg-transparent text-[13px] outline-none placeholder:text-stone-400" placeholder="告诉画布 Agent：补一个叶长歌单人反应镜头，保持雨夜冷色调…" />
              <div className="flex items-center pt-2"><button className="rounded-lg border border-[#ddd4c5] px-3 py-1.5 text-[12px] text-stone-500"><User size={13} className="mr-1 inline" />人物关系</button><button className="ml-auto flex h-9 w-9 items-center justify-center rounded-xl bg-[#8c7db0] text-white"><MessageSquare size={15} /></button></div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}